import React, { useMemo } from "react";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import { useParams } from "react-router-dom";

import RealTimeCard from "./RealTimeCard";
import { trailingAverage, useRealtimeChannels } from "./useRealtimeSeries";
import { baseChartOption, lineSeries } from "../charts/chartTheme";
import { EM_DASH, describe } from "../../utils/format";

/* Channel colours follow the emitter they are named after. Red and green are
   the weakest pair under protanopia, so each channel also gets a distinct line
   type — identity never rests on hue alone. */
const CHANNELS = [
  { key: "red", label: "Red", color: "#e34948", lineType: "solid" },
  { key: "ir", label: "Infrared", color: "#4a3aa7", lineType: "dashed" },
  { key: "green", label: "Green", color: "#008300", lineType: "dotted" },
];

const RealTimePPG = () => {
  const { watchId } = useParams();

  const paths = useMemo(
    () => ({
      channels: {
        green: `Health/${watchId}/realTime/ppg/ppgGreen`,
        ir: `Health/${watchId}/realTime/ppg/ppgIr`,
        red: `Health/${watchId}/realTime/ppg/ppgRed`,
      },
      status: `Health/${watchId}/realTime/ppg/ppgStatusDescription`,
      progress: `Health/${watchId}/realTime/ppg/ppgProgress`,
    }),
    [watchId]
  );

  const { samples, channels, status, progress } = useRealtimeChannels(
    watchId,
    paths
  );

  const stats = useMemo(
    () =>
      describe(
        [...(channels.red ?? []), ...(channels.ir ?? []), ...(channels.green ?? [])],
        0
      ),
    [channels]
  );

  const hasData = samples.length > 0;

  const buildOption = () =>
    baseChartOption({
      categories: samples,
      series: CHANNELS.map((channel) =>
        lineSeries({
          name: channel.label,
          data: channels[channel.key] ?? [],
          color: channel.color,
          lineType: channel.lineType,
          area: false,
          smooth: 0.15,
          width: 1.6,
          showSymbol: false,
        })
      ),
      showLegend: true,
      xName: "Sample",
      tooltipFormatter: (params) => {
        if (!params?.length) return "";
        const rows = params
          .map(
            (p) =>
              `<div style="display:flex;gap:8px;justify-content:space-between">
                 <span>${p.marker} ${p.seriesName}</span>
                 <strong>${p.value ?? "—"}</strong>
               </div>`
          )
          .join("");
        return `Sample ${params[0].axisValue}${rows}`;
      },
    });

  return (
    <RealTimeCard
      title="Live photoplethysmography"
      icon={<GraphicEqOutlinedIcon />}
      accent={CHANNELS[2].color}
      exportName="ppg"
      status={status}
      progress={progress}
      hasData={hasData}
      waitingCopy="The watch is streaming the red, infrared and green channels. Waveforms appear as samples arrive."
      deps={[channels, samples]}
      buildOption={buildOption}
      stats={[
        ...CHANNELS.map((channel) => ({
          label: `${channel.label} avg`,
          value: trailingAverage(channels[channel.key] ?? [], 49, 0),
          meta: "Last second",
        })),
        {
          label: "Samples",
          value: hasData ? samples.length : EM_DASH,
          meta: `Amplitude ${stats.range}`,
        },
      ]}
    />
  );
};

export default RealTimePPG;
